import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface PieSegment {
  label: string;
  value: number;
  color: string;
  icon?: React.ReactNode;
}

interface Animated3DPieChartProps {
  title: string;
  data: PieSegment[];
  size?: number;
}

const Animated3DPieChart: React.FC<Animated3DPieChartProps> = ({ 
  title, 
  data,
  size = 300 
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const total = data.reduce((sum, segment) => sum + segment.value, 0);
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.35;
  const holeRadius = size * 0.18;

  // Calculate segments
  let currentAngle = -90;
  const segments = data.map((segment, index) => {
    const percentage = (segment.value / total) * 100;
    const angle = (segment.value / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    // Calculate path for donut segment
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    const x3 = centerX + holeRadius * Math.cos(endRad);
    const y3 = centerY + holeRadius * Math.sin(endRad);
    const x4 = centerX + holeRadius * Math.cos(startRad);
    const y4 = centerY + holeRadius * Math.sin(startRad);

    const largeArc = angle > 180 ? 1 : 0;

    const path = `
      M ${x1} ${y1}
      A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${holeRadius} ${holeRadius} 0 ${largeArc} 0 ${x4} ${y4}
      Z
    `;

    // Calculate label position
    const midAngle = (startAngle + endAngle) / 2;
    const midRad = (midAngle * Math.PI) / 180;
    const labelRadius = radius + 40;
    const labelX = centerX + labelRadius * Math.cos(midRad);
    const labelY = centerY + labelRadius * Math.sin(midRad);

    return {
      ...segment,
      path,
      percentage,
      angle,
      startAngle,
      endAngle,
      labelX,
      labelY,
      midAngle,
    };
  });

  const getTrendIcon = (value: number) => {
    if (value > 33) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (value < 20) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
      <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">{title}</h3>

      <div className="flex flex-col lg:flex-row items-center gap-8">
        {/* 3D Pie Chart */}
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="drop-shadow-2xl">
            {/* 3D Shadow/Depth Effect */}
            <g opacity="0.3">
              {segments.map((segment, index) => (
                <motion.path
                  key={`shadow-${index}`}
                  d={segment.path}
                  fill={segment.color}
                  transform={`translate(0, 8)`}
                  filter="blur(4px)"
                />
              ))}
            </g>

            {/* Main Segments */}
            {segments.map((segment, index) => {
              const isHovered = hoveredIndex === index;
              const isSelected = selectedIndex === index;
              const isActive = isHovered || isSelected;

              return (
                <g key={index}>
                  <motion.path
                    d={segment.path}
                    fill={segment.color}
                    stroke="white"
                    strokeWidth="3"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ 
                      opacity: 1, 
                      scale: isActive ? 1.1 : 1,
                      translateX: isActive ? Math.cos((segment.midAngle * Math.PI) / 180) * 10 : 0,
                      translateY: isActive ? Math.sin((segment.midAngle * Math.PI) / 180) * 10 : 0,
                    }}
                    transition={{ 
                      duration: 0.8, 
                      delay: index * 0.1,
                      scale: { duration: 0.3 },
                      translateX: { duration: 0.3 },
                      translateY: { duration: 0.3 },
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onClick={() => setSelectedIndex(selectedIndex === index ? null : index)}
                    className="cursor-pointer"
                    style={{
                      filter: isActive ? 'brightness(1.1) drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'none',
                    }}
                  />

                  {/* Percentage Label on Segment */}
                  {segment.percentage > 5 && (
                    <motion.text
                      x={centerX + (radius - holeRadius) / 2 * Math.cos((segment.midAngle * Math.PI) / 180) + holeRadius * Math.cos((segment.midAngle * Math.PI) / 180)}
                      y={centerY + (radius - holeRadius) / 2 * Math.sin((segment.midAngle * Math.PI) / 180) + holeRadius * Math.sin((segment.midAngle * Math.PI) / 180)}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="text-sm font-bold fill-white pointer-events-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 + 0.5 }}
                    >
                      {segment.percentage.toFixed(1)}%
                    </motion.text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Center Label */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="text-center bg-white rounded-full p-6 shadow-lg">
              <div className="text-4xl font-black text-gray-900">{data.length}</div>
              <div className="text-sm text-gray-500 font-medium">Segments</div>
            </div>
          </motion.div>
        </div>

        {/* Legend & Details */}
        <div className="flex-1 space-y-3 max-w-md">
          {segments.map((segment, index) => {
            const isHovered = hoveredIndex === index;
            const isSelected = selectedIndex === index;
            const isActive = isHovered || isSelected;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => setSelectedIndex(selectedIndex === index ? null : index)}
                className={`
                  p-4 rounded-xl cursor-pointer transition-all duration-300
                  ${isActive
                    ? 'bg-gradient-to-r from-gray-50 to-gray-100 shadow-lg scale-105 border-2'
                    : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }
                `}
                style={{
                  borderColor: isActive ? segment.color : 'transparent',
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    {/* Color Indicator */}
                    <motion.div
                      className="w-4 h-4 rounded-full shadow-md"
                      style={{ backgroundColor: segment.color }}
                      animate={{
                        scale: isActive ? 1.3 : 1,
                        boxShadow: isActive
                          ? `0 0 20px ${segment.color}80`
                          : '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    />

                    {/* Label */}
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{segment.label}</div>
                      <div className="text-sm text-gray-500">
                        {segment.value.toLocaleString()} units
                      </div>
                    </div>

                    {/* Trend Icon */}
                    <div>{getTrendIcon(segment.percentage)}</div>
                  </div>

                  {/* Percentage */}
                  <div className="text-right ml-4">
                    <div className="text-2xl font-bold text-gray-900">
                      {segment.percentage.toFixed(1)}%
                    </div>
                  </div>
                </div>

                {/* Animated Progress Bar */}
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: segment.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${segment.percentage}%` }}
                    transition={{ duration: 1, delay: index * 0.1 + 0.3 }}
                  />
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-3 pt-3 border-t border-gray-200"
                    >
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center p-2 bg-white rounded-lg">
                          <div className="font-bold text-gray-900">{segment.value}</div>
                          <div className="text-gray-500">Value</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded-lg">
                          <div className="font-bold text-gray-900">{segment.angle.toFixed(0)}°</div>
                          <div className="text-gray-500">Angle</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded-lg">
                          <div className="font-bold text-gray-900">
                            {((segment.value / total) * 100).toFixed(1)}%
                          </div>
                          <div className="text-gray-500">Share</div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
          >
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {total.toLocaleString()}
                </div>
                <div className="text-xs text-gray-600">Total Value</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-indigo-600">
                  {(total / data.length).toFixed(0)}
                </div>
                <div className="text-xs text-gray-600">Average</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Animated3DPieChart;

